import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import ClientHeader from '../components/ClientHeader';
import NavSidebar from '../components/NavSidebar';
import { usePageMeta } from '../components/DashboardShell';
import { useView } from '../contexts/ViewContext';

import RunSelector from '../components/aim/RunSelector';
import { useAimRuns } from '../components/aim/lib/aimQueries';
import HeadlineMetrics from '../components/aim/HeadlineMetrics';
import AimWhatChangedCard from '../components/aim/AimWhatChangedCard';
import ProviderScorecard from '../components/aim/ProviderScorecard';
import OverviewTab from '../components/aim/tabs/OverviewTab';
import ProvidersTab from '../components/aim/tabs/ProvidersTab';
import CompetitorsTab from '../components/aim/tabs/CompetitorsTab';
import ContentTab from '../components/aim/tabs/ContentTab';
import DiagnosticsTab from '../components/aim/tabs/DiagnosticsTab';
import GoogleOrganicTab from '../components/aim/tabs/GoogleOrganicTab';
import SystemTab from '../components/aim/tabs/SystemTab';
import ProviderDetailPanel from '../components/aim/panels/ProviderDetailPanel';
import { getAimDefaultClientId } from '../components/aim/lib/defaultClient';
import { visibleTabs, resolveTab } from '../components/aim/lib/tabAccess';
import AimUpdateWarning from '../components/aim/AimUpdateWarning';
import { SkeletonDashboard } from '../components/shared/Skeleton';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'providers', label: 'Providers' },
  { key: 'competitors', label: 'Competitors' },
  { key: 'content', label: 'Content' },
  { key: 'diagnostics', label: 'Diagnostics' },
  { key: 'google', label: 'Google & Organic' },
  { key: 'system', label: 'System' },
];

export default function AimDashboardPage() {
  usePageMeta({ headerActive: 'AIM', sidebarActive: 'aim' });
  const view = useView();
  const username = localStorage.getItem('username') || 'User';
  const isClient = view.isClient;

  // AIM follows whatever the global header has selected; the URL never carries
  // `client` so refreshing the page can't re-introduce a stale selection. Run +
  // compare were previously URL-persisted too, but a stale runId from a prior
  // client breaks the dashboard when the global header switches projects — so
  // they now live in React state only.
  const clientId = view.supabaseClientId || getAimDefaultClientId();
  const clientDisplayName = view.clientDisplayName;

  // AI Monitor is run-keyed, not date-keyed: the header date-range picker is
  // disabled on this route (Epic 2), so the page ignores `rangeStart`/`rangeEnd`
  // entirely and shows every run for the property. The only empty state left is
  // "this property has never been run" — there is no "no runs in this date range"
  // state, because there is no date range on this page to be empty.
  const { data: runs, loading: runsLoading } = useAimRuns(clientId, 100);
  const noRunsAtAll = Boolean(clientId) && !runsLoading
    && Array.isArray(runs) && runs.length === 0;

  const [searchParams, setSearchParams] = useSearchParams();
  // System is admin-only: hidden from the tab bar, and a non-admin who opens
  // ?tab=system through a link lands on Overview instead.
  const activeTab = resolveTab(searchParams.get('tab') || 'overview', view.isAdmin);
  const tabs = visibleTabs(TABS, view.isAdmin);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [compareRunId, setCompareRunId] = useState('');
  const selectedProviderId = searchParams.get('provider') || '';
  const selectedProviderName = searchParams.get('providerName') || '';
  const [selectedProviderRow, setSelectedProviderRow] = useState(null);

  // Reset run + compare whenever the active client changes — runIds aren't
  // portable across clients, so the previous selection would otherwise point
  // at a run that doesn't belong to the newly selected project.
  useEffect(() => {
    setSelectedRunId('');
    setCompareRunId('');
  }, [clientId]);

  const updateParam = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null || value === '') next.delete(key);
      else next.set(key, value);
      return next;
    });
  }, [setSearchParams]);

  const setActiveTab = (tab) => updateParam('tab', tab);
  const setRun = (runId) => setSelectedRunId(runId || '');
  const setCompare = (runId) => setCompareRunId(runId || '');
  const openProvider = (provider) => {
    setSelectedProviderRow(provider);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const id = provider.provider_id || provider.id || '';
      const name = provider.provider_name || provider.display_name || '';
      if (id) next.set('provider', id);
      if (name) next.set('providerName', name);
      return next;
    });
  };
  const closeProvider = () => {
    setSelectedProviderRow(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('provider');
      next.delete('providerName');
      return next;
    });
  };

  // The URL only carries `tab` and the provider drilldown params. Strip any
  // leftover `client`, `run`, or `compare` params on mount so stale bookmarked
  // URLs can't reintroduce them.
  useEffect(() => {
    setSearchParams((prev) => {
      if (!prev.has('client') && !prev.has('run') && !prev.has('compare')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('client');
      next.delete('run');
      next.delete('compare');
      return next;
    });
  }, [setSearchParams]);

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case 'providers':   return <ProvidersTab clientId={clientId} runId={selectedRunId} onSelectProvider={openProvider} />;
      case 'competitors': return <CompetitorsTab clientId={clientId} runId={selectedRunId} />;
      case 'content':     return <ContentTab clientId={clientId} runId={selectedRunId} />;
      case 'diagnostics': return <DiagnosticsTab clientId={clientId} runId={selectedRunId} />;
      case 'google':      return <GoogleOrganicTab clientId={clientId} runId={selectedRunId} />;
      case 'system':      return <SystemTab clientId={clientId} runId={selectedRunId} onSelectRun={(id) => { setRun(id); setActiveTab('overview'); }} />;
      case 'overview':
      default:            return <OverviewTab clientId={clientId} runId={selectedRunId} compareRunId={compareRunId} />;
    }
  }, [activeTab, clientId, selectedRunId, compareRunId]); // eslint-disable-line

  const HeaderComp = isClient ? ClientHeader : AdminHeader;

  return (
    <>
        <main className="flex-1 min-w-0">
          <div className="px-4 md:px-6 lg:px-10 py-6 space-y-6">
            {/* ---- Page Header ---- */}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">AI Monitor</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Brand visibility across AI providers, competitor benchmarks, content coverage, and Google organic performance.
              </p>
            </div>

            {clientId && selectedRunId && (
              <AimWhatChangedCard
                clientId={clientId}
                runId={selectedRunId}
                compareRunId={compareRunId}
              />
            )}

            {/* Admin-only: the three-month refresh warning, from the property's
                full run list so the due date doesn't move with the date range. */}
            {view.isAdmin && clientId && !runsLoading && (
              <AimUpdateWarning runs={runs} clientId={clientId} />
            )}

            {/* Run Selector — replaces the global date picker on this page */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3">
              <RunSelector
                clientId={clientId}
                selectedRunId={selectedRunId}
                compareRunId={compareRunId}
                onChangeRun={setRun}
                onChangeCompare={setCompare}
              />
            </div>

            {/* Headline metrics + provider scorecard always visible */}
            {clientId ? (
              runsLoading ? (
                <SkeletonDashboard cards={3} />
              ) : noRunsAtAll ? (
                <div className="w-full bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 p-8 text-center shadow-sm">
                  <svg className="w-12 h-12 mx-auto text-[#32B1FF] mb-4" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13 2.5l1.9 5.6 5.6 1.9-5.6 1.9-1.9 5.6-1.9-5.6L5.5 10l5.6-1.9L13 2.5z" />
                    <path d="M19 14.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    No AI Monitor runs for this property
                  </h2>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {clientDisplayName ? `"${clientDisplayName}" has no runs` : 'The selected project has no runs'} yet. Pick another property in the header, or wait for its first scheduled run.
                  </p>
                </div>
              ) : (
                <>
                  <HeadlineMetrics clientId={clientId} runId={selectedRunId} compareRunId={compareRunId} />
                  <ProviderScorecard clientId={clientId} runId={selectedRunId} compareRunId={compareRunId} onSelectProvider={openProvider} />
                </>
              )
            ) : (
              <div className="w-full bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 p-8 text-center shadow-sm">
                <svg className="w-12 h-12 mx-auto text-[#32B1FF] mb-4" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 2.5l1.9 5.6 5.6 1.9-5.6 1.9-1.9 5.6-1.9-5.6L5.5 10l5.6-1.9L13 2.5z" />
                  <path d="M19 14.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2z" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  AI Monitor is not available for this property
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {clientDisplayName ? `"${clientDisplayName}" does not have AI Monitor data.` : 'The selected project does not have AI Monitor data.'} Switch to another property in the header to load the AI Monitor dashboard.
                </p>
              </div>
            )}

            {/* Tab bar */}
            {clientId && !runsLoading && !noRunsAtAll && (
              <div className="border-b border-gray-200 dark:border-gray-800">
                <div className="flex flex-wrap gap-1">
                  {tabs.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setActiveTab(t.key)}
                      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        activeTab === t.key
                          ? 'border-[#32B1FF] text-[#32B1FF]'
                          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {clientId && !runsLoading && !noRunsAtAll && tabContent}
          </div>
        </main>

      {selectedProviderId && (
        <ProviderDetailPanel
          clientId={clientId}
          runId={selectedRunId}
          providerId={selectedProviderId}
          providerName={selectedProviderName}
          scorecardRow={selectedProviderRow}
          onClose={closeProvider}
        />
      )}
    </>
  );
}
